import React from 'react';
import SchoolLogo from './SchoolLogo';

/**
 * The masthead: crest, school name, where it is, and the two things a parent
 * came to do. The featured photograph sits beside the type as a plate rather
 * than behind it — the type never competes with an unpredictable image, and the
 * page keeps one light ground from top to bottom.
 */
const SchoolProfileHero = ({ profile, onApplyClick }) => {
  const { schoolName, logoUrl, address, phone, website, featuredImageUrl } = profile;

  return (
    <header className="sp-masthead">
      <div className="sp-shell sp-masthead-inner">
        <div className="sp-masthead-type sp-rise">
          <div className="sp-masthead-crest">
            <SchoolLogo schoolName={schoolName} logoUrl={logoUrl} size={72} />
            <p className="sp-label">School profile</p>
          </div>

          <h1 className="sp-display sp-display-xl sp-masthead-name">{schoolName}</h1>

          <dl className="sp-masthead-meta">
            {address && (
              <div>
                <dt className="sp-label">Where we are</dt>
                <dd>{address}</dd>
              </div>
            )}
            {website && (
              <div>
                <dt className="sp-label">Online</dt>
                <dd>
                  <a
                    className="sp-inline-link"
                    href={website.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit the ${schoolName} website (opens in a new tab)`}
                  >
                    {website.label}
                  </a>
                </dd>
              </div>
            )}
          </dl>

          <div className="sp-actions sp-masthead-actions">
            <button type="button" className="sp-btn sp-btn-primary" onClick={onApplyClick}>
              Apply for Admission
            </button>

            {phone && (
              <a
                className="sp-link-action"
                href={phone.href}
                aria-label={`Call ${schoolName} on ${phone.display}`}
              >
                Call {phone.display}
              </a>
            )}
          </div>
        </div>

        <div className="sp-masthead-plate sp-rise">
          {featuredImageUrl ? (
            <img
              src={featuredImageUrl}
              alt={`${schoolName} campus`}
              className="sp-plate-image"
              decoding="async"
            />
          ) : (
            <div className="sp-plate-blank" aria-hidden="true" />
          )}
        </div>
      </div>

      <style>{`
        .sp-masthead {
          border-bottom: 1px solid var(--sp-rule);
        }

        .sp-masthead-inner {
          display: grid;
          grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
          gap: clamp(2rem, 5vw, 4.5rem);
          align-items: end;
          padding-top: clamp(2.5rem, 6vw, 5rem);
          padding-bottom: clamp(2.5rem, 6vw, 5rem);
        }

        .sp-masthead-crest {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .sp-masthead-name {
          margin-top: clamp(1.5rem, 3vw, 2.25rem);
        }

        .sp-masthead-meta {
          margin: clamp(1.75rem, 3vw, 2.5rem) 0 0 0;
          display: flex;
          flex-wrap: wrap;
          gap: 1.25rem 3rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--sp-rule);
        }

        .sp-masthead-meta dd {
          margin: 0.4rem 0 0 0;
          font-size: 1rem;
          color: var(--sp-ink-soft);
          overflow-wrap: anywhere;
        }

        .sp-masthead-actions {
          margin-top: clamp(1.75rem, 3vw, 2.5rem);
        }

        /* The photograph is a plate: squared off, hairline framed, no overlay. */
        .sp-masthead-plate {
          position: relative;
          aspect-ratio: 4 / 5;
          background: var(--sp-paper-raised);
          border: 1px solid var(--sp-rule);
          overflow: hidden;
          animation-delay: 0.08s;
        }

        .sp-plate-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* No photograph: a quiet tinted panel keeps the composition balanced. */
        .sp-plate-blank {
          width: 100%;
          height: 100%;
          background: radial-gradient(
              120% 90% at 78% 12%,
              var(--sp-accent-soft) 0%,
              transparent 62%
            ),
            var(--sp-paper-raised);
        }

        @media (max-width: 940px) {
          .sp-masthead-inner {
            grid-template-columns: 1fr;
            align-items: stretch;
          }

          .sp-masthead-plate {
            aspect-ratio: 16 / 9;
            order: -1;
          }
        }

        @media (max-width: 600px) {
          .sp-masthead-meta {
            gap: 1rem 2rem;
          }
        }
      `}</style>
    </header>
  );
};

export default SchoolProfileHero;
